import React, { useCallback, useRef, useState } from 'react';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import SubregionMap from '../SubregionMap';
import AssignToSubregionPopover from '../AssignToSubregionPopover';
import SubregionDetailsPopover from './SubregionDetailsPopover';
import AddressRadiusModal from './AddressRadiusModal';
import { unitToMeters, findZipsInRadius, getCircleBounds } from './utils';

const MapColumn = ({ state, loading, callbacks, isOnwardAdmin, carriersForLeg, subregionDetailsOpen }) => {
    const mapRef = useRef(null);
    const [root, setRoot] = useState(null);
    const initRoot = useCallback((node) => {
        if (node !== null) setRoot(node);
    }, []);

    const [radiusInputOpen, setRadiusInputOpen] = useState(false);

    const handleRadiusConfirm = ({ center, minRadius, maxRadius, unit }) => {
        const minMeters = unitToMeters(minRadius, unit);
        const maxMeters = unitToMeters(maxRadius, unit);
        setRadiusInputOpen(false);
        if (!mapRef.current) return;
        mapRef.current.fitBounds(getCircleBounds(center, maxMeters), { padding: 80 });
        mapRef.current.once('idle', () => {
            const zips = findZipsInRadius(mapRef.current, center, minMeters, maxMeters);
            if (zips.length === 0) return;
            callbacks.selectObject((prev) => {
                const next = { ...(prev.zip || {}) };
                for (const zip of zips) {
                    next[zip] = true;
                }
                return { ...prev, zip: next };
            });
        });
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex: 1;
                flex-basis: 0;
                overflow: hidden;
                position: relative;
            `}
        >
            <div
                ref={initRoot}
                css={css`
                    width: 100%;
                    height: 100%;
                    display: flex;
                `}
            />
            <SubregionMap
                ref={mapRef}
                node={root}
                subregions={state.subregions}
                callbacks={{ ...callbacks, onLoad: () => {} }}
                highlightOverride={state.highlightOverride}
                selectedSubregion={state.selectedSubregion}
                hoveredSubregion={state.hoveredSubregion}
                selectedZips={state.selectedZips}
                hoveredSubregionZip={state.hoveredSubregionZip}
                radiusControl={{
                    callbacks: {
                        onOpen: () => setRadiusInputOpen(true),
                    },
                }}
            />
            <AddressRadiusModal
                open={radiusInputOpen}
                onClose={() => setRadiusInputOpen(false)}
                onConfirm={handleRadiusConfirm}
            />
            {subregionDetailsOpen ? (
                <SubregionDetailsPopover
                    subregion={state.selectedSubregion}
                    collapsed={state.collapsed}
                    carriersForLeg={carriersForLeg}
                    loading={loading}
                    callbacks={callbacks}
                    isOnwardAdmin={isOnwardAdmin}
                />
            ) : null}
            {state.selectedZips.length > 0 ? (
                <AssignToSubregionPopover
                    loading={loading}
                    subregions={state.subregions}
                    selectedZips={state.selectedZips}
                    callbacks={callbacks}
                />
            ) : null}
        </Grid>
    );
};

export default MapColumn;
